import { z } from 'zod';
import { defineListQuery, listFilter, MAX_PAGE_SIZE } from './list-query.schemas';
import type { SuggestionWeights } from './organization.schemas';

const isoInstant = z.iso.datetime({ offset: true });

/** Every factor a suggestion is scored by; the keys of the company's weights, so a new factor is a new weight. */
export const SUGGESTION_FACTOR_KEYS = [
  'proximity',
  'freeAt',
  'emptyKm',
  'serviceDistance',
  'familiarity',
  'coupling',
] as const satisfies readonly (keyof SuggestionWeights)[];
export type SuggestionFactorKey = (typeof SUGGESTION_FACTOR_KEYS)[number];

/**
 * `GET /trips/:id/assignment-suggestions` (gitaumoses4/chukua#52). The window is the picker's, because neither the job nor the trip carries a
 * pickup time yet. `pageSize` left out takes the company's setting, unlike every other list, which has a fixed default.
 */
export const assignmentSuggestionQuerySchema = defineListQuery({
  sortFields: ['rank'],
  defaultSort: { sort: 'rank', order: 'asc' },
  filters: {
    windowStart: isoInstant.transform((value) => new Date(value)),
    windowEnd: isoInstant.transform((value) => new Date(value)),
    truckClass: listFilter(z.string().trim().min(1).max(32)),
  },
})
  .extend({ pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).optional() })
  .refine((query) => query.windowEnd > query.windowStart, { message: 'The window must end after it starts', path: ['windowEnd'] });
export type AssignmentSuggestionQuery = z.output<typeof assignmentSuggestionQuerySchema>;

/** One factor of a suggestion: its score from 0 to 1, or null when its input does not exist yet (it then counts as neutral), and the words for it. */
export interface SuggestionFactorView {
  key: SuggestionFactorKey;
  score: number | null;
  detail: string;
}

/**
 * Where the crew's trailer is now (gitaumoses4/chukua#390). `on_another_truck` is a warning on the crew, not a block: the trailer has to be swapped over
 * in the yard. A trailer on a truck that is out on a trip is blocked, so it never reaches a suggestion.
 */
export interface SuggestionTrailerAvailability {
  state: 'parked' | 'on_this_truck' | 'on_another_truck';
  coupledTruckRegistration: string | null;
}

/** A truck, trailer and driver to offer for a trip, with the reasons it ranks where it does. `verdict` is the worst of every eligibility check on the three. */
export interface AssignmentSuggestion {
  rank: number;
  verdict: 'pass' | 'warn';
  /** Whole points, 0 to 100, from the company's weights as they stood when the list was made. */
  score: number;
  truck: { id: string; registration: string; classCode: string };
  trailer: { id: string; registration: string; availability: SuggestionTrailerAvailability };
  driver: { id: string; name: string };
  factors: SuggestionFactorView[];
  warnings: string[];
  reason: string;
}
